import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

const defaultGetOptionId = (option) => option.id;
const defaultGetOptionLabel = (option) => option.label;
const defaultGetOptionDescription = (option) => option.description;

export default function MultiSelectPopover({
  label,
  selectedValues = [],
  options = [],
  onApply,
  searchLabel = 'Search options',
  emptyLabel = 'No options available',
  placeholder = 'Select options',
  applyLabel = 'Apply',
  cancelLabel = 'Cancel',
  clearLabel = 'Clear',
  closeLabel = 'Close',
  getOptionId = defaultGetOptionId,
  getOptionLabel = defaultGetOptionLabel,
  getOptionDescription = defaultGetOptionDescription,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingValues, setPendingValues] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const open = Boolean(anchorEl);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return options;

    return options.filter((option) => {
      const labelText = String(getOptionLabel(option) || '').toLowerCase();
      const descriptionText = String(getOptionDescription(option) || '').toLowerCase();
      return labelText.includes(normalizedQuery) || descriptionText.includes(normalizedQuery);
    });
  }, [getOptionDescription, getOptionLabel, options, searchQuery]);

  const selectedLabels = selectedValues
    .map((value) => options.find((option) => getOptionId(option) === value))
    .filter(Boolean)
    .map((option) => getOptionLabel(option));

  const handleOpen = (event) => {
    setPendingValues([...selectedValues]);
    setSearchQuery('');
    setAnchorEl(event.currentTarget);
  };

  const handleCancel = () => {
    setPendingValues([...selectedValues]);
    setAnchorEl(null);
  };

  const handleApply = () => {
    onApply([...pendingValues]);
    setAnchorEl(null);
  };

  const toggleValue = (value) => {
    setPendingValues((current) =>
      current.includes(value)
        ? current.filter((currentValue) => currentValue !== value)
        : [...current, value]
    );
  };

  return (
    <Box sx={{ mt: 2, width: '100%' }}>
      <Typography component="div" variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Button
        type="button"
        fullWidth
        variant="outlined"
        onClick={handleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          minHeight: 56,
          justifyContent: 'space-between',
          color: 'text.primary',
          fontWeight: 400,
          textAlign: 'left',
          textTransform: 'none',
          overflow: 'hidden',
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder}
        </Box>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          role: 'dialog',
          'aria-label': label,
          sx: {
            width: anchorEl?.clientWidth || 360,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'min(420px, calc(100vh - 32px))',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ p: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
            {label}
          </Typography>
          <IconButton size="small" onClick={handleCancel} aria-label={closeLabel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ px: 1.5, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            label={searchLabel}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {filteredOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
            {emptyLabel}
          </Typography>
        ) : (
          <List dense sx={{ overflow: 'auto', flex: 1, py: 0, px: 1 }}>
            {filteredOptions.map((option) => {
              const value = getOptionId(option);
              const optionLabel = getOptionLabel(option);
              const checked = pendingValues.includes(value);

              return (
                <ListItemButton
                  key={value}
                  selected={checked}
                  disabled={option.disabled}
                  onClick={() => toggleValue(value)}
                  sx={{ minHeight: 48, borderRadius: 1 }}
                >
                  <Checkbox
                    checked={checked}
                    disabled={option.disabled}
                    inputProps={{ 'aria-label': optionLabel }}
                    sx={{ mr: 1 }}
                  />
                  <ListItemText
                    primary={optionLabel}
                    secondary={getOptionDescription(option) || undefined}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button type="button" onClick={() => setPendingValues([])} color="inherit">
            {clearLabel}
          </Button>
          <Button type="button" onClick={handleCancel} color="inherit">
            {cancelLabel}
          </Button>
          <Button type="button" onClick={handleApply} variant="contained">
            {applyLabel}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}